/**
 * Secure error handling utilities
 * Prevents information disclosure while maintaining good user experience
 */

interface ErrorContext {
  userId?: string;
  sessionId?: string;
  route?: string;
  action?: string;
  timestamp: number;
  userAgent: string;
  additionalInfo?: Record<string, unknown>;
}

interface SecureError {
  id: string;
  message: string;
  type: 'validation' | 'authentication' | 'authorization' | 'network' | 'system' | 'user';
  severity: 'low' | 'medium' | 'high' | 'critical';
  userMessage: string;
  technicalDetails?: string;
  context: ErrorContext;
  stack?: string;
}

interface ErrorHandlingConfig {
  logSensitiveErrors: boolean;
  showStackTrace: boolean;
  maxErrorsPerSession: number;
  rateLimitWindow: number; // minutes
  enableCrashReporting: boolean;
}

/** Prefix on this handler's own log line, so it can recognise and skip it. */
const SECURE_LOG_PREFIX = 'Secure Error Log:';

class SecureErrorHandler {
  private config: ErrorHandlingConfig = {
    logSensitiveErrors: process.env.NODE_ENV === 'development',
    showStackTrace: process.env.NODE_ENV === 'development',
    maxErrorsPerSession: 50,
    rateLimitWindow: 5, // minutes
    enableCrashReporting: process.env.NODE_ENV === 'production'
  };

  private errorCounts = new Map<string, { count: number; firstOccurrence: number; lastOccurrence: number }>();
  private sessionErrors: SecureError[] = [];

  /**
   * The real `console.error`, captured before this class replaces it.
   *
   * WITHOUT THIS THE HANDLER ATE ITS OWN TAIL (fixed 2026-09-05).
   * `initializeErrorHandling` replaces `console.error` with one that calls
   * `handleError`, and `handleError` finishes by calling
   * `logErrorSecurely`, which called `console.error` — the replaced one. So
   * every console.error recursed: log → handle → log → handle, until
   * `RangeError: Maximum call stack size exceeded`.
   *
   * The per-error rate limiter did not save it, because each recursion built a
   * new message ("Secure Error Log: {…}" wrapping the previous one) and
   * therefore a new error id, so nothing was ever recognised as a repeat.
   *
   * The visible symptom was the whole app dying: in development a single
   * console.error — say Firebase reporting an incomplete config — blew the
   * stack before React mounted, and the page sat on its static loading
   * spinner forever with no error shown. Found while verifying the Vite
   * migration; it predates it.
   */
  private readonly nativeConsoleError: typeof console.error = console.error.bind(console);

  /** Guards against re-entering the handler from inside its own logging. */
  private handling = false;
  private sensitiveDataPatterns = [
    /sk-[a-zA-Z0-9]{40,}/g, // API keys
    /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}/g, // Email addresses
    /\b\d{4}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{4}\b/g, // Credit card numbers
    /\b\d{3}-\d{2}-\d{4}\b/g, // SSN format
    /password[=:]\s*['"]?[^'"\s]+['"]?/gi, // Passwords
    /token[=:]\s*['"]?[^'"\s]+['"]?/gi, // Tokens
    /secret[=:]\s*['"]?[^'"\s]+['"]?/gi, // Secrets
    /key[=:]\s*['"]?[^'"\s]+['"]?/gi // Keys
  ];

  constructor() {
    this.initializeErrorHandling();
  }

  /**
   * Initialize global error handling
   */
  private initializeErrorHandling(): void {
    // Global error handler
    window.addEventListener('error', (event) => {
      this.handleError(event.error || new Error(event.message), {
        route: window.location.pathname,
        action: 'global_error_handler',
        additionalInfo: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno
        }
      });
    });

    // Unhandled promise rejection handler
    window.addEventListener('unhandledrejection', (event) => {
      this.handleError(
        new Error(`Unhandled promise rejection: ${event.reason}`),
        {
          route: window.location.pathname,
          action: 'unhandled_promise_rejection',
          additionalInfo: {
            reason: event.reason
          }
        }
      );
    });

    // Console error interception (for development). The replacement calls
    // `this.nativeConsoleError`, never the patched function, so the two cannot
    // chase each other.
    if (this.config.logSensitiveErrors) {
      console.error = (...args) => {
        this.handleConsoleError(args);
        this.nativeConsoleError(...args);
      };
    }
  }

  /**
   * Handle console errors
   */
  private handleConsoleError(args: unknown[]): void {
    const message = args.map(arg => String(arg)).join(' ');
    // Our own log line, should it ever reach here, is not an error to report.
    if (message.startsWith(SECURE_LOG_PREFIX)) {
      return;
    }
    this.handleError(new Error(message), {
      route: window.location.pathname,
      action: 'console_error'
    });
  }

  /**
   * Main error handling method
   */
  public handleError(error: Error, context: Partial<ErrorContext> = {}): SecureError {
    // Re-entrancy guard. Belt to the braces of using the native console:
    // anything this method calls that itself errors must not come back round.
    if (this.handling) {
      return this.createSecureError(error, context);
    }
    this.handling = true;
    try {
      return this.handleErrorInternal(error, context);
    } finally {
      this.handling = false;
    }
  }

  private handleErrorInternal(error: Error, context: Partial<ErrorContext> = {}): SecureError {
    const secureError = this.createSecureError(error, context);
    
    // Rate limiting check
    if (!this.shouldProcessError(secureError.id)) {
      return secureError;
    }

    // Log error securely
    this.logErrorSecurely(secureError);

    // Track error for session
    this.trackSessionError(secureError);

    // Send to external error reporting if enabled
    if (this.config.enableCrashReporting && secureError.severity !== 'low') {
      this.sendToCrashReporting(secureError);
    }

    return secureError;
  }

  /**
   * Create a secure error object
   */
  private createSecureError(error: Error, context: Partial<ErrorContext>): SecureError {
    const errorId = this.generateErrorId();
    const sanitizedMessage = this.sanitizeErrorMessage(error.message);
    const errorType = this.classifyError(error);
    const severity = this.determineSeverity(error, errorType);
    const userMessage = this.generateUserFriendlyMessage(errorType, severity);

    const fullContext: ErrorContext = {
      userId: context.userId,
      sessionId: this.getSessionId(),
      route: context.route || window.location.pathname,
      action: context.action || 'unknown',
      timestamp: Date.now(),
      userAgent: navigator.userAgent,
      additionalInfo: context.additionalInfo || {}
    };

    return {
      id: errorId,
      message: sanitizedMessage,
      type: errorType,
      severity,
      userMessage,
      technicalDetails: this.config.showStackTrace ? error.stack : undefined,
      context: fullContext,
      stack: this.config.logSensitiveErrors ? error.stack : undefined
    };
  }

  /**
   * Generate unique error ID
   */
  private generateErrorId(): string {
    // Log correlation id shown to the user so support can find the entry.
    // eslint-disable-next-line no-restricted-syntax
    return `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Sanitize error message to remove sensitive information
   */
  private sanitizeErrorMessage(message: string): string {
    let sanitized = message;

    // Remove sensitive data patterns
    this.sensitiveDataPatterns.forEach(pattern => {
      sanitized = sanitized.replace(pattern, '[REDACTED]');
    });

    // Remove file paths that might expose system structure
    sanitized = sanitized.replace(/\/[^/\s]+\/[^/\s]+\/[^/\s]+/g, '[PATH_REDACTED]');

    // Remove URLs that might contain sensitive parameters
    sanitized = sanitized.replace(/https?:\/\/[^\s]+/g, '[URL_REDACTED]');

    return sanitized;
  }

  /**
   * Classify error type
   */
  private classifyError(error: Error): SecureError['type'] {
    const message = error.message.toLowerCase();
    const name = error.name.toLowerCase();

    if (message.includes('network') || message.includes('fetch')) {
      return 'network';
    }
    
    if (message.includes('unauthorized') || message.includes('forbidden') || name.includes('auth')) {
      return 'authentication';
    }
    
    if (message.includes('permission') || message.includes('access denied')) {
      return 'authorization';
    }
    
    if (message.includes('validation') || message.includes('invalid')) {
      return 'validation';
    }
    
    if (message.includes('user') || message.includes('input')) {
      return 'user';
    }

    return 'system';
  }

  /**
   * Determine error severity
   */
  private determineSeverity(error: Error, type: SecureError['type']): SecureError['severity'] {
    const message = error.message.toLowerCase();

    // Critical errors
    if (message.includes('security') || message.includes('csrf') || message.includes('xss')) {
      return 'critical';
    }

    // High severity
    if (type === 'authentication' || type === 'authorization' || message.includes('crash')) {
      return 'high';
    }

    // Medium severity
    if (type === 'network' || type === 'system') {
      return 'medium';
    }

    // Low severity
    return 'low';
  }

  /**
   * Generate user-friendly error message
   */
  private generateUserFriendlyMessage(type: SecureError['type'], severity: SecureError['severity']): string {
    const messages = {
      validation: {
        low: 'Please check your input and try again.',
        medium: 'There was a problem with the information provided.',
        high: 'Unable to process your request. Please verify your input.',
        critical: 'Request cannot be processed due to data validation issues.'
      },
      authentication: {
        low: 'Please log in to continue.',
        medium: 'Your session has expired. Please log in again.',
        high: 'Authentication failed. Please check your credentials.',
        critical: 'Security verification failed. Please contact support.'
      },
      authorization: {
        low: 'You don\'t have permission to perform this action.',
        medium: 'Access denied. You may not have the required permissions.',
        high: 'Insufficient privileges to access this resource.',
        critical: 'Access blocked due to security policy violation.'
      },
      network: {
        low: 'Network connection issue. Please try again.',
        medium: 'Unable to connect to the server. Please check your connection.',
        high: 'Service temporarily unavailable. Please try again later.',
        critical: 'Critical network error. Please contact support.'
      },
      system: {
        low: 'A minor issue occurred. Please try again.',
        medium: 'System temporarily unavailable. Please try again shortly.',
        high: 'An unexpected error occurred. Please contact support if this persists.',
        critical: 'Critical system error. Our team has been notified.'
      },
      user: {
        low: 'Please review your input and try again.',
        medium: 'There was an issue with your request.',
        high: 'Unable to complete your request. Please try again.',
        critical: 'Your request could not be processed. Please contact support.'
      }
    };

    return messages[type][severity];
  }

  /**
   * Get current session ID
   */
  private getSessionId(): string {
    // Try to get from session storage or generate new one
    let sessionId = sessionStorage.getItem('session_id');
    if (!sessionId) {
      // Telemetry correlation id — not an auth session.
      // eslint-disable-next-line no-restricted-syntax
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('session_id', sessionId);
    }
    return sessionId;
  }

  /**
   * Check if error should be processed (rate limiting)
   */
  private shouldProcessError(errorId: string): boolean {
    const now = Date.now();
    const windowMs = this.config.rateLimitWindow * 60 * 1000;

    // Clean up old entries
    Array.from(this.errorCounts.entries()).forEach(([id, data]) => {
      if (now - data.firstOccurrence > windowMs) {
        this.errorCounts.delete(id);
      }
    });

    // Check rate limit
    const errorData = this.errorCounts.get(errorId);
    if (errorData) {
      if (errorData.count >= this.config.maxErrorsPerSession) {
        return false;
      }
      errorData.count++;
      errorData.lastOccurrence = now;
    } else {
      this.errorCounts.set(errorId, {
        count: 1,
        firstOccurrence: now,
        lastOccurrence: now
      });
    }

    return true;
  }

  /**
   * Log error securely
   */
  private logErrorSecurely(error: SecureError): void {
    const logData: Record<string, unknown> = {
      id: error.id,
      type: error.type,
      severity: error.severity,
      message: error.message,
      route: error.context.route,
      action: error.context.action,
      timestamp: error.context.timestamp,
      userId: error.context.userId || 'anonymous'
    };

    // Include stack trace only in development
    if (this.config.logSensitiveErrors && error.stack) {
      logData.stack = error.stack;
    }

    // The NATIVE console.error — see `nativeConsoleError`. Using the patched
    // one here is what made this method call itself forever.
    this.nativeConsoleError(SECURE_LOG_PREFIX, logData);
  }

  /**
   * Track error for current session
   */
  private trackSessionError(error: SecureError): void {
    this.sessionErrors.push(error);

    // Keep only recent errors to prevent memory issues
    if (this.sessionErrors.length > this.config.maxErrorsPerSession) {
      this.sessionErrors.shift();
    }
  }

  /**
   * Send error to external crash reporting service
   */
  private sendToCrashReporting(error: SecureError): void {
    // This would integrate with services like Sentry, LogRocket, etc.
    // For now, we'll simulate the call
    const sentryWindow = window as Window & {
      Sentry?: { captureException: (error: Error, options?: unknown) => void };
    };
    if (typeof window !== 'undefined' && sentryWindow.Sentry) {
      sentryWindow.Sentry.captureException(new Error(error.message), {
        tags: {
          errorType: error.type,
          severity: error.severity
        },
        extra: {
          errorId: error.id,
          route: error.context.route,
          action: error.context.action
        }
      });
    } else {
      // Fallback logging
      console.warn('Would send to crash reporting:', {
        id: error.id,
        message: error.message,
        type: error.type,
        severity: error.severity
      });
    }
  }

  /**
   * Get error statistics for current session
   */
  public getErrorStats(): {
    totalErrors: number;
    errorsByType: Record<string, number>;
    errorsBySeverity: Record<string, number>;
    recentErrors: SecureError[];
  } {
    const errorsByType: Record<string, number> = {};
    const errorsBySeverity: Record<string, number> = {};

    this.sessionErrors.forEach(error => {
      errorsByType[error.type] = (errorsByType[error.type] || 0) + 1;
      errorsBySeverity[error.severity] = (errorsBySeverity[error.severity] || 0) + 1;
    });

    return {
      totalErrors: this.sessionErrors.length,
      errorsByType,
      errorsBySeverity,
      recentErrors: this.sessionErrors.slice(-10)
    };
  }

  /**
   * Clear session errors
   */
  public clearSessionErrors(): void {
    this.sessionErrors = [];
    this.errorCounts.clear();
  }

  /**
   * Create error for API responses
   */
  public createApiError(response: Response, context?: Partial<ErrorContext>): SecureError {
    const error = new Error(`API Error: ${response.status} ${response.statusText}`);
    return this.handleError(error, {
      ...context,
      action: 'api_call',
      additionalInfo: {
        status: response.status,
        url: response.url,
        headers: Object.fromEntries(response.headers.entries())
      }
    });
  }

  /**
   * Handle validation errors
   */
  public handleValidationError(field: string, message: string, context?: Partial<ErrorContext>): SecureError {
    const error = new Error(`Validation Error: ${field} - ${message}`);
    return this.handleError(error, {
      ...context,
      action: 'validation',
      additionalInfo: {
        field,
        validationMessage: message
      }
    });
  }
}

// Global secure error handler instance
export const secureErrorHandler = new SecureErrorHandler();

// React hook for error handling
export const useSecureErrorHandler = () => {
  return {
    handleError: secureErrorHandler.handleError.bind(secureErrorHandler),
    createApiError: secureErrorHandler.createApiError.bind(secureErrorHandler),
    handleValidationError: secureErrorHandler.handleValidationError.bind(secureErrorHandler),
    getErrorStats: secureErrorHandler.getErrorStats.bind(secureErrorHandler),
    clearSessionErrors: secureErrorHandler.clearSessionErrors.bind(secureErrorHandler)
  };
};

// Export types
export type { SecureError, ErrorContext, ErrorHandlingConfig };